import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

interface SidebarContextType {
  collapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
}

// localStorageから初期値を取得
const getInitialCollapsedState = (): boolean => {
  try {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (stored !== null) {
      return JSON.parse(stored) as boolean;
    }
  } catch {
    // localStorageが使用できない場合やパースエラーは無視
  }
  return false;
};

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState<boolean>(getInitialCollapsedState);

  // 状態が変更されたらlocalStorageに保存
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(collapsed));
    } catch {
      // localStorageが使用できない場合は無視
    }
  }, [collapsed]);

  const toggleSidebar = (): void => {
    setCollapsed(!collapsed);
  };

  const setSidebarCollapsed = (newCollapsed: boolean): void => {
    setCollapsed(newCollapsed);
  };

  return (
    <SidebarContext.Provider
      value={{
        collapsed,
        toggleSidebar,
        setSidebarCollapsed,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
